/**
 * The uploader's label map (UPL-09): the kinds of annotation the files bring whose label the
 * catalog lacks, or has for another shape type, and what each kind can become: a catalog label of
 * its shape type, with a choice answered or not, a free-text class under its old name (LBL-08), or
 * nothing. A label never joins the catalog here; a catalog editor adds it first (LBL-02).
 */
import { foreignKey, type Foreign, type LabelTarget, type Overrides } from '../anonymization/engine'
import { freeTextLabel, spelled } from '../annotation/freeText'
import type { Label } from '../lib/types'
import type { Item } from './pipeline'

/** A foreign kind across the files: its annotations, and in how many files they are. */
export interface ForeignKind extends Foreign {
  key: string
  files: number
}

export function foreignKinds(items: readonly Item[]): ForeignKind[] {
  const kinds = new Map<string, ForeignKind>()
  for (const found of items.flatMap((item) => item.analysis?.foreign ?? [])) {
    const key = foreignKey(found)
    const known = kinds.get(key)
    kinds.set(key, known ? { ...known, count: known.count + found.count, files: known.files + 1 } : { ...found, key, files: 1 })
  }
  return [...kinds.values()].sort((a, b) => a.key.localeCompare(b.key))
}

/** What a foreign kind can become; `answer` is a choice attribute's value the label takes. */
export type Choice =
  | { id: string; kind: 'free'; target: LabelTarget }
  | { id: string; kind: 'label'; label: string; answer?: readonly [string, string]; target: LabelTarget }
  | { id: string; kind: 'none'; target: null }

/**
 * What a foreign kind can become: the free-text class of its old name, spelled as the catalog's names
 * are, where anything of the name remains; the catalog's labels offered for new annotations, of the
 * kind's shape type; nothing last.
 */
export function choices(catalog: readonly Label[], kind: Foreign): Choice[] {
  const labels = catalog.filter((label) => label.type === kind.type && !label.hidden && !label.system)
  const free = spelled(kind.label)
  return [
    ...(free ? [{ id: freeTextLabel(free), kind: 'free' as const, target: { label: freeTextLabel(free) } }] : []),
    ...labels.flatMap((label): Choice[] => [
      { id: label.name, kind: 'label', label: label.name, target: { label: label.name } },
      ...label.attributes
        .filter((attribute) => attribute.input_type === 'radio' || attribute.input_type === 'select')
        .flatMap((attribute) =>
          attribute.values.map((value): Choice => ({
            id: `${label.name}:${attribute.name}=${value}`,
            kind: 'label',
            label: label.name,
            answer: [attribute.name, value],
            target: { label: label.name, attributes: { [attribute.name]: value } },
          })),
        ),
    ]),
    // No label name has a dash, so this id is nothing else's.
    { id: '-', kind: 'none', target: null },
  ]
}

/** Corrections added to a file's earlier ones (ANON-06); label maps add up, so kinds mapped in turn all stay mapped. */
export function withCorrections(before: Overrides | undefined, added: Overrides): Overrides {
  return { ...before, ...added, ...(added.labelMap && { labelMap: { ...before?.labelMap, ...added.labelMap } }) }
}
